<template>
  <div class="markers-wrapper">
    <div ref="canvasWrapper" :style="bgStyle">
      <PolygonControls
        v-if="activeElementType === 'marker' || activeElementType === 'dot'"
        :scale="scale"
      />

      <div v-for="item in propertyMarkers" :key="item._id">
        <div
          v-if="item.marker"
          :id="item._id"
          class="dot"
          :class="{
            'dot--selected': isActive(item._id),
            inactive: isInactive(item._id),
          }"
          :style="{
            left: item.marker.x * scale + 'px',
            top: item.marker.y * scale + 'px',
          }"
          @pointerdown="onMouseDown(item, null, $event)"
          @pointerup="onMouseUp($event)"
          @pointermove="onMouseDrag($event, null)"
          @dblclick="onDoubleClick(item, null, $event)"
        >
          <div class="dot__label">{{ item.label }}</div>
        </div>
      </div>

      <div
        v-for="item in [...subsectionMarkers, ...livingOptionMarkers]"
        :key="item._id"
      >
        <tag-basic
          v-for="(marker, i) in markerList(item)"
          :key="`${item._id}|${i}`"
          v-bind="marker"
          :_id="`${item._id}|${i}`"
          :class="{
            active: isActive(item._id),
            inactive: isInactive(item._id),
          }"
          :pos="{
            x: marker.x,
            y: marker.y,
          }"
          :label="
            (item.displayName && item.displayName[activeLanguage.code]) ||
            (item.displayName && item.displayName['en']) ||
            item.label
          "
          :style="`transform: rotate(${marker.rotation}deg);`"
          @pointerdown.native="onMouseDown(item, i, $event)"
          @pointerup.native="onMouseUp($event)"
          @pointermove.native="onMouseDrag($event, i)"
          @dblclick.native="onDoubleClick(item, i, $event)"
        />
      </div>

      <reference-tag-slider
        v-if="referenceTagsSlider.length > 0"
        :isVertical="true"
        :scaledAmount="scale"
        :tagRef="referenceTagsSlider"
        @click.native.stop="
          {
          }
        "
      />
    </div>
  </div>
</template>

<script>
import { orderBy } from "lodash";
import { mapState } from "vuex";
import { mapGetters } from "vuex";
import TagBasic from "@/components/TagBasic";
import PolygonControls from "@/components/PolygonControls";

export default {
  props: [
    "value",
    "selected-elements",
    "property-ids",
    "subsection-ids",
    "living-option-ids",
    "background-image",
    "zoom-level",
  ],

  components: {
    TagBasic,
    PolygonControls,
  },

  data() {
    return {
      scale: 1,
      draggableMarker: null,

      clickOffsetX: null,
      clickOffsetY: null,
    };
  },

  computed: {
    ...mapState("draggable", ["activeElement", "activeElementType"]),

    ...mapGetters([
      "activeLanguage",
      "page",
      "properties",
      "subsections",
      "livingOptions",
    ]),

    tags: {
      get() {
        return this.value;
      },
      set(tags) {
        this.$emit("input", tags);
      },
    },

    propertyMarkers() {
      if (this.propertyIds) {
        return this.properties.filter((property) =>
          this.propertyIds.includes(property._id)
        );
      }

      return [];
    },

    subsectionMarkers() {
      if (this.subsectionIds) {
        return this.subsections.filter((subsection) =>
          this.subsectionIds.includes(subsection._id)
        );
      }

      return [];
    },

    livingOptionMarkers() {
      if (this.livingOptionIds) {
        return this.livingOptions.filter(
          (option) => this.livingOptionIds.includes(option._id) && option.active
        );
      }

      return [];
    },

    bgStyle() {
      if (this.scale) {
        return {
          ["font-size"]: 20 * this.scale + "px",
          ["height"]: 1960 * this.scale + "px",
        };
      }
      return {};
    },

    referenceTagsSlider() {
      if (this.tags) {
        let refTags = this.tags.filter(function (tag) {
          return tag.type == "reference";
        });

        refTags = orderBy(refTags, ["pos.x"], ["asc"]);

        if (this.dir === "rtl") {
          let startingIndex = refTags.length;

          refTags.forEach((tag, index) => {
            if (!tag.hideTagReference) {
              tag.referenceIndex = startingIndex--;
            }
          });
          return refTags;
        } else {
          refTags.forEach((tag, index) => {
            if (!tag.hideTagReference) {
              tag.referenceIndex = index + 1;
            }
          });
          return refTags;
        }
      } else {
        return [];
      }
    },
  },

  watch: {
    zoomLevel() {
      this.getScaledAmount();
    },

    propertyMarkers: {
      handler(markers) {
        (markers || []).forEach((item) =>
          this.ensureVisibleMarkers(item, false)
        );
      },
      immediate: true,
    },

    subsectionMarkers: {
      handler(markers) {
        (markers || []).forEach((item) =>
          this.ensureVisibleMarkers(item, true)
        );
      },
      immediate: true,
    },

    livingOptionMarkers: {
      handler(markers) {
        (markers || []).forEach((item) =>
          this.ensureVisibleMarkers(item, true)
        );
      },
      immediate: true,
    },
  },

  mounted() {
    window.addEventListener("resize", this.getScaledAmount);
    this.$nextTick(() => {
      this.getScaledAmount();
    });
  },

  methods: {
    getTagBoundingBox(tagEl, type) {
      // Some tags are structured differently and won't work properly
      // Basic icon, Reveal, Box
      const canvasHeight = 1960;
      const canvasWidth = 3190;
      const canvasLayer = document
        .querySelector(".draggable-container")
        .getBoundingClientRect();

      const elementClasses = [
        ".tag-dot",
        ".tag-rich",
        ".tag-rich__asset-col",
        ".tag-basic",
        ".tag-action",
        ".tag-ref",
      ];

      const selectorString = elementClasses.join(", ");
      const children = tagEl.querySelectorAll(selectorString);
      const elements = [tagEl, ...Array.from(children)];

      const tagAnchorRect = tagEl.getBoundingClientRect();

      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;

      // Loop through all elements and update the min/max coordinates
      elements.forEach((element) => {
        const rect = element.getBoundingClientRect();
        if (rect.width > 0 || rect.height > 0) {
          minX = Math.min(minX, rect.left);
          minY = Math.min(minY, rect.top);
          maxX = Math.max(maxX, rect.right);
          maxY = Math.max(maxY, rect.bottom);
        }
      });

      // tag distance from the canvas edge in pixels
      const leftDistance = minX - canvasLayer.left;
      const rightDistance = canvasLayer.right - maxX;
      const topDistance = minY - canvasLayer.top;
      const bottomDistance = canvasLayer.bottom - maxY;

      // anchor position relative to tag element
      const anchorLeft = tagAnchorRect.left - canvasLayer.left;
      const anchorRight = canvasLayer.right - tagAnchorRect.right;
      const anchorTop = tagAnchorRect.top - canvasLayer.top;
      const anchorBottom = canvasLayer.bottom - tagAnchorRect.bottom;

      // determine the bounds
      const anchorXMin = Math.round((anchorLeft - leftDistance) / this.scale);
      const anchorXMax =
        canvasWidth - Math.round((anchorRight - rightDistance) / this.scale);
      const anchorYMin = Math.round((anchorTop - topDistance) / this.scale);
      const anchorYMax =
        canvasHeight - Math.round((anchorBottom - bottomDistance) / this.scale);

      return {
        xMin: anchorXMin,
        xMax: anchorXMax,
        yMin: anchorYMin,
        yMax: anchorYMax,
        x: tagAnchorRect.left,
        y: tagAnchorRect.top,
      };
    },

    markerList(item) {
      if (!item || !Array.isArray(item.markers)) return [];
      return item.markers;
    },

    // initial position of new markers
    ensureVisibleMarkers(item, usesPluralMarkers) {
      if (!item) return;

      const markers = usesPluralMarkers ? this.markerList(item) : [item.marker];
      markers.filter(Boolean).forEach((marker) => {
        const isUnplaced =
          (marker.x === 0 || marker.x == null) &&
          (marker.y === 0 || marker.y == null);

        if (isUnplaced) {
          const { x, y } = this.clampToCanvas(200, 100);
          this.$set(marker, "x", x);
          this.$set(marker, "y", y);
        }
      });
    },

    // shared clamping helper
    clampToCanvas(x, y) {
      if (!this.$refs.canvasWrapper || !this.scale) {
        return { x, y };
      }

      const canvasWidth = this.$refs.canvasWrapper.offsetWidth;
      const canvasHeight = this.$refs.canvasWrapper.offsetHeight;

      const maxX = canvasWidth / this.scale;
      const maxY = canvasHeight / this.scale;

      const clampedX = Math.max(0, Math.min(x, maxX));
      const clampedY = Math.max(0, Math.min(y, maxY));

      return {
        x: Math.round(clampedX),
        y: Math.round(clampedY),
      };
    },

    getScaledAmount() {
      // default size based on 3190x1960 - scale = 1
      if (this.$refs.canvasWrapper) {
        let canvasWidth =
          this.$refs.canvasWrapper.getBoundingClientRect().width;
        let scaled = canvasWidth / 3190;
        this.scale = scaled;
      }
    },

    onMouseDrag(event, index) {
      if (!this.draggableMarker) return;

      let tagEl = null;

      if (index !== null) {
        tagEl = document.getElementById(`${this.activeElement._id}|${index}`);
      } else {
        tagEl = document.getElementById(this.activeElement._id);
      }

      if (!tagEl) return;

      const tagBounds = this.getTagBoundingBox(tagEl, this.activeElement.type);

      const canvasEl = document.querySelector(".draggable-container__bg");
      const canvasRect = canvasEl.getBoundingClientRect();

      if (!this.clickOffsetX) {
        this.clickOffsetX = tagBounds.x - event.pageX;
      }
      if (!this.clickOffsetY) {
        this.clickOffsetY = tagBounds.y - event.pageY;
      }

      const layerX =
        (event.clientX - canvasRect.left + this.clickOffsetX) / this.scale;
      const layerY =
        (event.clientY - canvasRect.top + this.clickOffsetY) / this.scale;

      const cx = Math.min(Math.max(layerX, tagBounds.xMin), tagBounds.xMax);
      const cy = Math.min(Math.max(layerY, tagBounds.yMin), tagBounds.yMax);

      this.draggableMarker.x = cx;
      this.draggableMarker.y = cy;
    },

    addTag() {
      this.$store.dispatch("GET_TAG_SCHEMA").then((res) => {
        this.tags.push(res.data);
      });
    },

    openAssetLibrary(context, assetSelected) {
      this.$emit("openAssetLibrary", context, assetSelected);
    },

    onMouseDown(item, index, e) {
      e.target.setPointerCapture(e.pointerId);

      if (index !== null) {
        this.draggableMarker = this.markerList(item)[index];
      } else {
        this.draggableMarker = item.marker;
      }

      if (!this.draggableMarker) return;

      this.$store.commit("draggable/addToSelectedElements", {
        element: item,
        elementType: index !== null ? "marker" : "dot",
        markerIndex: index,
        isSingle: true, //!e.shiftKey,
      });

      this.$emit("record-before-action", item);
    },

    onMouseUp(e) {
      e.target.releasePointerCapture(e.pointerId);
      this.draggableMarker = null;
      this.clickOffsetX = null;
      this.clickOffsetY = null;
    },

    isActive(id) {
      const item = this.selectedElements.find((element) => element._id === id);
      return !_.isEmpty(item);
    },

    isInactive(id) {
      if (!this.selectedElements.length) return false;

      const item = this.selectedElements.find((element) => element._id === id);
      return _.isEmpty(item);
    },

    openMenu() {
      this.$store.commit("draggable/setIsMenuOpen", true);
    },

    onDoubleClick(item, index, event) {
      // shift+double click to select all markers
      if (event && event.shiftKey) {
        this.$emit("select-all-markers");
        return;
      }

      // always make this marker the single active selection on dblclick
      this.$store.commit("draggable/addToSelectedElements", {
        element: item,
        elementType: index !== null ? "marker" : "dot",
        markerIndex: index,
        isSingle: true,
      });

      // property markers should NEVER open the edit menu
      const isPropertyMarker =
        Array.isArray(this.propertyIds) && this.propertyIds.includes(item._id);

      if (isPropertyMarker) {
        this.$store.commit("draggable/setIsMenuOpen", false); // optional safety
        return;
      }

      // if somehow multi-selected, don't open
      if (this.selectedElements && this.selectedElements.length > 1) {
        return;
      }

      this.$store.commit("draggable/setIsMenuOpen", true);
    },
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.getScaledAmount);
  },
};
</script>

<style lang="scss" scoped>
.markers-wrapper {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
}

.dot {
  border-radius: 50%;
  background-color: red;
  cursor: pointer;
  pointer-events: all;
  position: absolute;
  font-size: 0.75em;
  height: 3em;
  width: 3em;
  user-select: none;
  opacity: 1;
  transform: translate(-0.25em, -0.4em);
  z-index: 6;

  &__label {
    background: #fff;
    bottom: 3.5em;
    left: 50%;
    opacity: 0;
    padding: 0.5em;
    pointer-events: none;
    position: absolute;
    transform: translateX(-50%);
    width: max-content;
  }

  &:hover {
    .dot__label {
      opacity: 1;
    }
  }

  &--selected {
    border: 2px solid #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.4);
  }
}

.inactive {
  opacity: 0.5;
}
</style>
